import React, { useState } from 'react';
import moment from 'moment';
import { Calendar, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import usePlayfulTeacher from '@/hooks/usePlayfulTeacher';
import { LESSON_STATUS, LESSON_STATUS_LABEL } from './lesson-plan-smart-constants';
import { LessonPlanSmartLessonDialog } from './lesson-plan-smart-lesson-dialog';

const STATUS_BADGE_VARIANT = {
  [LESSON_STATUS.NEXT]: 'amber',
  [LESSON_STATUS.PLANNED]: 'success'
};

// Variantes "-soft" são o padrão de badge de status do tema playful.
const PLAYFUL_STATUS_BADGE_VARIANT = {
  [LESSON_STATUS.NEXT]: 'amber-soft',
  [LESSON_STATUS.PLANNED]: 'success-soft'
};

function formatTime(time) {
  const parsed = moment(time, 'HH:mm');
  return parsed.minutes() === 0 ? parsed.format('H[h]') : parsed.format('H[h]mm');
}

function formatLessonTime(lesson) {
  if (lesson.startTime && lesson.endTime) return `${formatTime(lesson.startTime)} às ${formatTime(lesson.endTime)}`;
  if (lesson.startTime) return formatTime(lesson.startTime);
  return 'Horário a definir';
}

export function LessonPlanSmartLessonCard({ lesson, subject }) {
  const playfulTeacher = usePlayfulTeacher();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const isNext = lesson.status === LESSON_STATUS.NEXT;

  const header = (
    <div className="flex w-full items-center justify-between gap-2">
      <div className={cn('flex items-center gap-1', playfulTeacher ? 'text-neutral-500' : 'text-primary-dark')}>
        <Calendar className="size-3.5 shrink-0" />
        <span className="text-sm font-bold">{moment(lesson.date).format('DD/MM')}</span>
      </div>
      <div className={cn('flex items-center gap-1', playfulTeacher ? 'text-neutral-500' : 'text-primary-dark')}>
        <Clock className="size-3.5 shrink-0" />
        <span className="text-sm font-bold">{formatLessonTime(lesson)}</span>
      </div>
    </div>
  );

  const body = (
    <div className="flex w-full flex-col gap-3">
      {lesson.status === LESSON_STATUS.DONE ? (
        playfulTeacher ? (
          <Badge
            variant="muted"
            size="xs"
            className="w-fit shrink-0 self-start text-sm font-bold"
          >
            {LESSON_STATUS_LABEL[lesson.status]}
          </Badge>
        ) : (
          <span className="inline-block w-fit shrink-0 self-start truncate rounded-full bg-bright px-2 py-0.5 text-sm font-bold text-neutral-dark">{LESSON_STATUS_LABEL[lesson.status]}</span>
        )
      ) : (
        <Badge
          variant={(playfulTeacher ? PLAYFUL_STATUS_BADGE_VARIANT : STATUS_BADGE_VARIANT)[lesson.status]}
          size="xs"
          className="w-fit shrink-0 self-start text-sm font-bold"
        >
          {LESSON_STATUS_LABEL[lesson.status]}
        </Badge>
      )}

      <p
        title={lesson.objectOfKnowledge}
        className={cn('mb-0 line-clamp-2 text-sm font-semibold leading-normal', playfulTeacher ? 'text-neutral-900' : 'text-primary-dark')}
      >
        {lesson.objectOfKnowledge}
      </p>

      <p className="mb-0 truncate text-sm text-neutral-700">Aula {lesson.lessonNumber}</p>
    </div>
  );

  if (isNext) {
    return (
      <>
        <button
          type="button"
          onClick={() => setIsDialogOpen(true)}
          className="group flex h-full w-full flex-col gap-5 rounded-2xl border-[3px] border-warning bg-[#F8F1E7] p-4 text-left shadow-dropshadow-sm"
        >
          {header}
          {body}
          <div
            className={cn(
              'mt-auto flex w-full items-center justify-center gap-2 rounded-full px-4 py-2 text-white transition-[background-color,transform] duration-150 ease-out-strong group-active:scale-[0.97]',
              playfulTeacher ? 'group-hover:brightness-105' : 'bg-mission group-hover:bg-[#ee8445]'
            )}
            style={playfulTeacher ? { background: 'var(--gradient-btn-continue)', boxShadow: '0 3px 0 var(--color-mission-dark)' } : undefined}
          >
            <span className="text-sm font-bold">Mais detalhes</span>
          </div>
        </button>

        <LessonPlanSmartLessonDialog
          lesson={lesson}
          subject={subject}
          open={isDialogOpen}
          onOpenChange={setIsDialogOpen}
        />
      </>
    );
  }

  return (
    <div className="flex h-full w-full flex-col gap-5 rounded-2xl border border-neutral-lightest bg-white p-4 shadow-dropshadow-sm">
      {header}
      {body}
      <button
        type="button"
        onClick={() => setIsDialogOpen(true)}
        className={cn(
          'mt-auto flex w-full items-center justify-center gap-2 rounded-full border px-4 py-2 transition-[background-color,border-color,color,transform] duration-150 ease-out-strong active:scale-[0.97]',
          playfulTeacher ? 'border-[var(--color-mission)] text-[var(--color-mission)] hover:bg-[var(--color-mission)] hover:text-white' : 'border-primary-light text-primary hover:border-primary hover:bg-primary hover:text-white'
        )}
      >
        <span className="text-sm font-bold">Mais detalhes</span>
      </button>

      <LessonPlanSmartLessonDialog
        lesson={lesson}
        subject={subject}
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
      />
    </div>
  );
}
